import { CircularProgress } from '@mui/material';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Review360Me, review360Api } from 'src/api/review360';
import { useAppDispatch } from 'src/hooks/Redux/useAppDispatch';
import { setActiveTabIndex } from 'src/redux/slices/tabsSlice';
import useAppSelector from 'src/hooks/Redux/useAppSelector';
import { selectCurrentUser } from 'src/redux/slices/loggedUserSlice';

import { useTabActive } from '../Tabs/tabActiveContext';
import { FramedDashboard } from './FramedDashboard';
import styles from './Oth360Tab.module.scss';
import {
  LOADING_TEXT,
  UNAVAILABLE_TEXT,
  collectingText,
  notFoundText,
  unlockedText,
} from './oth360Text';

/**
 * How "Åpne min 360" opens the member's 360review dashboard. Decided by the
 * 360review frame spike (docs/briefs/oth-360-circle-tab.md section 3):
 *   true  = inside the tab, in a frame that 360review sizes through
 *           postMessage (`360review:height`) and leaves through
 *           `360review:back`;
 *   false = in a new browser tab (Option B, the fallback).
 * The 360review frame spike (PR 389 there, 2026-10-14) passed in Chrome at
 * desktop and phone width: render, tabs, AI report, PDF (opens a new tab on
 * touch devices), session after reload, height and back messages. WebKit was
 * not run by the spike; Markus checks on his iPhone on staging.
 */
export const EMBED_360 = true;

/** The circle's own content loads first; the 360 lookup follows shortly after. */
const PREFETCH_DELAY_MS = 1500;

type Oth360TabProps = {
  circleId: string;
};

type LoadState =
  { kind: 'loading' } | { kind: 'ready'; me: Review360Me } | { kind: 'error' };

/**
 * The member's own 360 inside the OTH circle. The dashboard link is a login
 * credential: it lives in this component's state only, is fetched when the tab
 * is opened and used once, when the member presses the button.
 */
export const Oth360Tab: FC<Oth360TabProps> = ({ circleId }) => {
  const isActive = useTabActive();
  const user = useAppSelector(selectCurrentUser);
  const dispatch = useAppDispatch();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  // In the frame variant the dashboard opens as soon as it is found (Markus,
  // 2026-10-14: no extra step); the status card is where the back link leads
  const [opened, setOpened] = useState<boolean>(EMBED_360);
  const requested = useRef(false);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Asked once per circle visit: right away when the tab is shown, otherwise
  // a moment after the circle has loaded (every tab panel is mounted, hidden),
  // so the tab is ready the moment the member opens it. The answer is kept
  // even if the member switches tabs while it is on its way.
  useEffect(() => {
    if (requested.current) {
      return undefined;
    }
    const ask = () => {
      if (requested.current) {
        return;
      }
      requested.current = true;
      review360Api
        .getMy360(circleId)
        .then((me) => {
          if (isMounted.current) {
            setState({ kind: 'ready', me });
          }
        })
        .catch(() => {
          if (isMounted.current) {
            setState({ kind: 'error' });
          }
        });
    };
    if (isActive) {
      ask();
      return undefined;
    }
    const timer = window.setTimeout(ask, PREFETCH_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [circleId, isActive]);

  const me = state.kind === 'ready' ? state.me : null;
  const dashboardUrl = me?.found ? me.dashboardUrl : null;

  // "Tilbake til Ordtilhandling" inside the frame: the circle's first tab,
  // since the dashboard itself is this tab (Markus, 2026-10-14)
  const backToCircle = useCallback(
    () => dispatch(setActiveTabIndex(0)),
    [dispatch]
  );

  const openDashboard = useCallback(() => {
    if (!dashboardUrl) {
      return;
    }
    if (EMBED_360) {
      setOpened(true);
    } else {
      window.open(dashboardUrl, '_blank', 'noopener');
    }
  }, [dashboardUrl]);

  const copyTeamLink = async (teamLink: string) => {
    try {
      await navigator.clipboard.writeText(teamLink);
      toast.success('Lenken er kopiert');
    } catch {
      toast.error('Kunne ikke kopiere lenken');
    }
  };

  if (state.kind === 'loading') {
    return (
      <section className={styles.container}>
        <div className={styles.loading} role="status" aria-live="polite">
          <CircularProgress
            size={40}
            sx={{ color: 'var(--brand-accent, #f0544f)' }}
          />
          <p className={styles.quiet}>{LOADING_TEXT}</p>
        </div>
      </section>
    );
  }

  if (
    state.kind === 'error' ||
    !me ||
    (!me.found && me.reason === 'no_email')
  ) {
    return (
      <section className={styles.container}>
        <div className={styles.card}>
          <p className={styles.quiet}>{UNAVAILABLE_TEXT}</p>
        </div>
      </section>
    );
  }

  if (!me.found) {
    return (
      <section className={styles.container}>
        <div className={styles.card}>
          <h2 className={styles.title}>Din 360</h2>
          <p className={styles.text}>{notFoundText(user?.email)}</p>
        </div>
      </section>
    );
  }

  if (EMBED_360 && opened && dashboardUrl) {
    return (
      <FramedDashboard
        src={dashboardUrl}
        title="Min 360"
        loadingText={LOADING_TEXT}
        onBack={backToCircle}
      />
    );
  }

  const { round, teamLink } = me;
  const heading =
    round.status === 'unlocked'
      ? 'Resultatene dine er klare'
      : round.status === 'closed'
        ? 'Din forrige 360 er avsluttet'
        : 'Din 360';
  const text =
    round.status === 'unlocked'
      ? unlockedText(round.respondents)
      : round.status === 'closed'
        ? 'Du finner resultatene i dashbordet ditt.'
        : collectingText(round.respondents, round.unlockAt);

  return (
    <section className={styles.container}>
      <div className={styles.card}>
        <h2 className={styles.title}>{heading}</h2>
        <p className={styles.text}>{text}</p>

        {round.status === 'collecting' && teamLink && (
          <div className={styles.shareBlock}>
            <label className={styles.shareLabel} htmlFor="oth360-team-link">
              Lenke til de som skal gi deg tilbakemelding
            </label>
            <div className={styles.shareRow}>
              <input
                id="oth360-team-link"
                className={styles.shareInput}
                type="text"
                readOnly
                value={teamLink}
                onFocus={(event) => event.currentTarget.select()}
              />
              <button
                type="button"
                className={styles.secondaryButton}
                onClick={() => copyTeamLink(teamLink)}
              >
                Kopier lenke
              </button>
            </div>
            <p className={styles.shareHint}>
              Del lenken med de du vil ha tilbakemelding fra.
            </p>
          </div>
        )}

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={openDashboard}
          >
            Åpne min 360
          </button>
        </div>
      </div>
    </section>
  );
};
